import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getSuperAdminCompanyClaims,
  PaginatedResponse,
  SuperAdminCompanyClaim,
} from "../../services/superAdminApi";

const badgeClass = (status?: string | null) => {
  const normalized = (status || "").toLowerCase();
  if (normalized === "approved") return "bg-green-100 text-green-700";
  if (normalized === "rejected") return "bg-red-100 text-red-700";
  return "bg-sky-100 text-sky-700";
};

const displayMessage = (message?: string | null) => {
  if (!message) return "-";
  const words = message.trim().split(/\s+/);
  return words.length > 20 ? `${words.slice(0, 20).join(" ")}...` : message;
};

const SuperAdminCompanyClaims: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SuperAdminCompanyClaim[]>([]);
  const [pagination, setPagination] =
    useState<PaginatedResponse<SuperAdminCompanyClaim> | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [perPage, setPerPage] = useState(15);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchRows = async (
    page = 1,
    overrides?: { search?: string; status?: string }
  ) => {
    try {
      setLoading(true);
      setError("");
      const res = await getSuperAdminCompanyClaims({
        page,
        per_page: perPage,
        search: (overrides?.search ?? search) || undefined,
        status: (overrides?.status ?? status) || undefined,
      });
      setRows(res.data || []);
      setPagination(res);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load company claims.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage]);

  const summary = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.total += 1;
        const current = (row.status || "").toLowerCase();
        if (current === "approved") acc.approved += 1;
        else if (current === "rejected") acc.rejected += 1;
        else acc.pending += 1;
        return acc;
      },
      { total: 0, approved: 0, pending: 0, rejected: 0 }
    );
  }, [rows]);

  const currentPage = pagination?.current_page ?? 1;
  const lastPage = pagination?.last_page ?? 1;

  return (
    <div className="space-y-5 p-1">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#402B69]">Company Claims</h1>
          <p className="text-sm text-gray-500">
            Web-matched claims list inside Glossa.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/superadmin/companies")}
          className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700"
        >
          Back to Companies
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Claims On Page</p>
          <p className="text-2xl font-bold text-[#402B69]">{summary.total}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Approved</p>
          <p className="text-2xl font-bold text-green-600">{summary.approved}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Pending</p>
          <p className="text-2xl font-bold text-sky-600">{summary.pending}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Rejected</p>
          <p className="text-2xl font-bold text-red-600">{summary.rejected}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 items-end">
        <div className="min-w-[240px]">
          <label className="block text-xs text-gray-500 mb-1">Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Company, user, email, phone..."
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Rows per page</label>
          <select
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value={15}>15</option>
            <option value={30}>30</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <button
          type="button"
          onClick={() => fetchRows(1)}
          className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 rounded-lg text-sm font-semibold"
        >
          Apply
        </button>
        <button
          type="button"
          onClick={() => {
            setSearch("");
            setStatus("");
            fetchRows(1, { search: "", status: "" });
          }}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold"
        >
          Reset
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-gray-600">Loading company claims...</div>
        ) : error ? (
          <div className="p-6 text-red-600">{error}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">#</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Company Name</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Company Address</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">User Name</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">User Email</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">User Phone</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">User Message</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-6 text-gray-500">
                        No company claims found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => (
                      <tr key={row.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                        <td className="px-4 py-3 text-gray-700">
                          {(currentPage - 1) * perPage + index + 1}
                        </td>
                        <td className="px-4 py-3 font-medium text-[#402B69]">
                          {row.company?.company_name || "-"}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{row.company?.address || "-"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.name || "-"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.email || "-"}</td>
                        <td className="px-4 py-3 text-gray-700">{row.phone || "-"}</td>
                        <td className="px-4 py-3 text-gray-700 max-w-[280px]">{displayMessage(row.message)}</td>
                        <td className="px-4 py-3 text-gray-700">
                          <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${badgeClass(row.status)}`}>
                            {row.status || "pending"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="border-t px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                Showing page {currentPage} of {lastPage}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchRows(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchRows(currentPage + 1)}
                  disabled={currentPage >= lastPage}
                  className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SuperAdminCompanyClaims;
